import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { FEATURE_FLAGS, flagEnabled, useAuth } from "../../hooks/useAuth";
import { useAdminClaws } from "../../hooks/useAdminClaws";
import { Skeleton } from "../../components/dashboard/Skeleton";
import { InstanceDetailHeader } from "../../components/admin/instance-detail/InstanceDetailHeader";
import { InstanceTabBar, tabPanelId } from "../../components/admin/instance-detail/InstanceTabBar";
import { InstanceTabPanel } from "../../components/admin/instance-detail/InstanceTabPanels";
import { parseRange, type InstanceRange } from "../../lib/instanceDetailRange";
import { parseTab, type InstanceTab } from "../../lib/instanceDetailTabs";
import styles from "../../components/admin/instance-detail/instanceDetail.module.css";

const DetailSkeleton = (): JSX.Element => (
  <div className={styles.page}>
    <div className={styles.skelHeader}>
      <Skeleton width="14rem" height="0.9rem" />
      <Skeleton width="22rem" height="1.6rem" />
      <Skeleton height="2.2rem" />
    </div>
    <div className={styles.kpiRow}>
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} height="3.6rem" />
      ))}
    </div>
    <div className={styles.panelGrid}>
      {Array.from({ length: 2 }, (_, i) => (
        <Skeleton key={i} height="11rem" />
      ))}
    </div>
  </div>
);

const NotFound = ({ tenantId }: { tenantId: string | undefined }): JSX.Element => (
  <div className={styles.page}>
    <nav className={styles.breadcrumb} aria-label="Breadcrumb">
      <Link to="/dashboard/admin/instances" className={styles.crumbLink}>Instances</Link>
    </nav>
    <p className={styles.notFound}>
      Instance <code className={styles.code}>{tenantId}</code> not found.{" "}
      <Link to="/dashboard/admin/instances">Back to Instances</Link>
    </p>
  </div>
);

export const AdminInstanceDetail = (): JSX.Element => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const billingEnabled = flagEnabled(user?.features, FEATURE_FLAGS.billingStripe);
  const range = parseRange(params.get("range"));
  const tab = parseTab(params.get("tab"), billingEnabled);
  const { status, claws, setUnlimitedEntitlements, setAgentAccess, setBonusSlots } = useAdminClaws();
  const claw = useMemo(() => claws.find((c) => c.tenantId === tenantId) ?? null, [claws, tenantId]);

  const setParam = (key: "range" | "tab", value: InstanceRange | InstanceTab): void => {
    setParams((prev) => {
      prev.set(key, value);
      return prev;
    }, { replace: true });
  };

  if (claw == null) return status === "loading" ? <DetailSkeleton /> : <NotFound tenantId={tenantId} />;

  // Stopped boxes stay RUNNING and a RESIZING box is an established tenant, so
  // both have history to show. Only PROVISIONING/FAILED have no data.
  const hasData = claw.status === "RUNNING" || claw.status === "RESIZING";

  return (
    <div className={styles.page}>
      <InstanceDetailHeader claw={claw} />
      {hasData ? (
        <>
          <InstanceTabBar tab={tab} onTabChange={(next) => setParam("tab", next)} billingEnabled={billingEnabled}
            range={range} onRangeChange={(next) => setParam("range", next)} />
          <div role="tabpanel" id={tabPanelId(tab)} aria-labelledby={`${tabPanelId(tab)}-tab`} className={styles.tabPanel}>
            <InstanceTabPanel tab={tab} claw={claw} range={range}
              adminActions={{ setUnlimitedEntitlements, setAgentAccess, setBonusSlots }} />
          </div>
        </>
      ) : (
        <>
          <p className={styles.notRunning}>
            {claw.status === "FAILED"
              ? "This instance failed to provision, so there is no operational data yet."
              : "Charts appear once this instance finishes provisioning."}
          </p>
          <InstanceTabPanel tab="settings" claw={claw} range={range}
            adminActions={{ setUnlimitedEntitlements, setAgentAccess, setBonusSlots }} />
        </>
      )}
    </div>
  );
};
